/**
 * @author Maxime Mustarda <maxime@inarix.com>
 * @file parseLicenceCredits.tsx
 * @desc Created on Wed Apr 30 2025 17:12:53
 * @copyright All rights reserved @ Inarix
 */

import { Box, Chip, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import { WarningAmber as WarningAmberIcon } from "@mui/icons-material";
import HelpIcon from "@mui/icons-material/Help";
import {
  ExtendedLicense,
  LicenseStatus,
  PackStatus,
} from "../../redux/actions/licenses/licenceTypes";
import { Colors } from "./LicenseFilterBtns";
import { TFunction } from "i18next";
import { PacksTooltipContent } from "./PacksTooltip";
import {
  cellHelpIconSx,
  cellValueWithIconSx,
  creditBoxSx,
  creditLabelStyle,
  packTooltipSlotProps,
} from "./licenseStyles/LicenceColumns.styles";

export function remainingCredsPct(li: ExtendedLicense) {
  const currPackCreds =
    (
      li.packs?.find((p) => p.statusConstantId === PackStatus.active) ||
      li.packs?.findLast((p) => p.statusConstantId === PackStatus.emptied)
    )?.initialCredits || 0;
  const nextCredits =
    li.packs?.reduce(
      (total, p) =>
        (total +=
          p.statusConstantId === PackStatus.bound &&
          new Date(p.expiryDate).getTime() > Date.now()
            ? p.initialCredits
            : 0),
      0,
    ) ?? 0;
  return {
    pct:
      (li.availableCredits + nextCredits) / (currPackCreds + nextCredits || 1),
    currPackCreds,
    nextCredits,
  };
}

export function nextBoundPack(li: ExtendedLicense) {
  return li.packs?.find(
    (p) =>
      p.statusConstantId === PackStatus.bound &&
      new Date(p.expiryDate).getTime() > Date.now(),
  );
}

// The pack a negative balance is charged against: the active pack if there
// is one, otherwise the next bound pack that will absorb the debt.
export function debtAbsorbingPack(li: ExtendedLicense) {
  return (
    li.packs?.find((p) => p.statusConstantId === PackStatus.active) ??
    nextBoundPack(li)
  );
}

// Credit totals for the monitoring columns:
// - remaining: what is left of the active pack plus upcoming bound packs
// - consumed: emptied packs plus what was already drawn from the active pack
//   (a negative balance counts as extra consumption)
// - purchased: every pack ever bought, whatever its status (metadata
//   fallback is stored in millicredits)
export function creditTotals(li: ExtendedLicense) {
  const packs = li.packs ?? [];
  const available = li.availableCredits ?? 0;
  const purchasedFromPacks = packs.reduce(
    (acc, p) => acc + p.initialCredits,
    0,
  );
  const purchased =
    purchasedFromPacks > 0
      ? purchasedFromPacks
      : (li.metadata?.totalCreditsSinceStart ?? 0) / 1000;
  const upcoming = packs.reduce(
    (acc, p) =>
      acc +
      (p.statusConstantId === PackStatus.bound &&
      new Date(p.expiryDate).getTime() > Date.now()
        ? p.initialCredits
        : 0),
    0,
  );
  const emptied = packs.reduce(
    (acc, p) =>
      acc + (p.statusConstantId === PackStatus.emptied ? p.initialCredits : 0),
    0,
  );
  const activePackCreds =
    packs.find((p) => p.statusConstantId === PackStatus.active)
      ?.initialCredits ?? 0;
  const remaining = available + upcoming;
  const consumed = Math.max(emptied + activePackCreds - available, 0);
  return { purchased, remaining, consumed };
}

// Renders the "remaining" value exactly as the licenses table's `remaining`
// column does, so the license Balance shown elsewhere (e.g. the add-credits
// modal) never drifts from what the table displays.
export function renderRemainingCredits(
  license: ExtendedLicense,
  t: TFunction,
  language: string,
) {
  if (!license) return null;
  const isExpired = new Date(license.expiryDate).getTime() - Date.now() <= 0;
  if (isExpired) {
    return <Chip label={t("licenses.expired")} variant="outlined" />;
  }
  if (licenseIsInfinite(license)) return <span>∞</span>;
  const { remaining } = creditTotals(license);
  if (remaining < 0) {
    return (
      <Tooltip
        title={
          <span>
            {t("licenses.debtTooltip")}{" "}
            <span style={{ fontWeight: "bold" }}>{t("licenses.addPack")}</span>
          </span>
        }
        placement="top"
      >
        <Chip
          icon={<WarningAmberIcon />}
          label={remaining.toLocaleString()}
          color="warning"
          variant="outlined"
          size="small"
        />
      </Tooltip>
    );
  }
  const packs = license.packs ?? [];
  const available = license.availableCredits ?? 0;
  const activePack = packs.find(
    (p) => p.statusConstantId === PackStatus.active,
  );
  const upcomingPacks = packs.filter(
    (p) =>
      p.statusConstantId === PackStatus.bound &&
      new Date(p.expiryDate).getTime() > Date.now(),
  );
  const calcRows: { label: string; value: number }[] = [];
  if (activePack || available !== 0) {
    calcRows.push({
      label: t("licenses.calc_remaining_active"),
      value: available,
    });
  }
  if (upcomingPacks.length) {
    calcRows.push({
      label: t("licenses.calc_upcoming_packs"),
      value: upcomingPacks.reduce((acc, p) => acc + p.initialCredits, 0),
    });
  }
  return (
    <Tooltip
      title={
        calcRows.length ? (
          <PacksTooltipContent
            packs={[]}
            t={t}
            language={language}
            calculation={{
              rows: calcRows,
              totalLabel: t("licenses.remaining"),
              total: remaining,
            }}
          />
        ) : (
          t("licenses.remainingDesc")
        )
      }
      placement="bottom-start"
      arrow
      slotProps={calcRows.length ? packTooltipSlotProps : undefined}
    >
      <Box sx={creditBoxSx}>
        <Box sx={cellValueWithIconSx}>
          <span style={creditLabelStyle}>{remaining.toLocaleString()}</span>
          <HelpIcon sx={cellHelpIconSx} />
        </Box>
      </Box>
    </Tooltip>
  );
}

export function licenseIsInfinite(li: ExtendedLicense) {
  return (
    new Date(li.expiryDate).getTime() > Date.now() &&
    li.licenseTemplate.parameters?.defaultScenarioCost === 0 &&
    !li.licenseTemplate.parameters?.specificScenarioCosts
  );
}

export function renderLicenseExpiration(
  licenses: ExtendedLicense[],
  t: TFunction<"global", undefined>,
  addTxt?: string,
  expireTxt?: string,
) {
  if (!licenses?.length && addTxt) {
    return <Link to="#">{addTxt}</Link>;
  }

  for (const license of licenses) {
    if (license.statusConstantId === LicenseStatus.expired) {
      continue;
    }

    if (licenseIsInfinite(license)) {
      // unlimited license
      return <span>∞</span>;
    }

    if (license.statusConstantId <= LicenseStatus.emptied) {
      const oneMonthFromNow = new Date();
      const { currPackCreds } = remainingCredsPct(license);

      return (
        <>
          {license.expiryDate &&
            new Date(license.expiryDate) < oneMonthFromNow && (
              <Tooltip title={t("licenses.expSoon_desc")}>
                <Chip
                  style={{
                    height: 10,
                    width: 10,
                    marginRight: 5,
                    backgroundColor: Colors.red,
                  }}
                  variant="filled"
                />
              </Tooltip>
            )}
          &nbsp;&nbsp;
          <span>{`${license.availableCredits} / ${currPackCreds}`}</span>
        </>
      );
    }
  }

  return <Chip label={expireTxt} variant="outlined" />;
}

export function renderLicenseExtrapack(
  licenses: ExtendedLicense[],
  addTxt?: string,
  expireTxt?: string,
) {
  if (!licenses?.length && addTxt) {
    return <Link to="#">{addTxt}</Link>;
  }

  for (const license of licenses) {
    if (new Date(license.expiryDate).getTime() - Date.now() <= 0) {
      continue;
    }

    if (licenseIsInfinite(license)) {
      // unlimited license
      return <span>∞</span>;
    }

    if (
      license.statusConstantId <= LicenseStatus.emptied ||
      new Date(license.expiryDate).getTime() > Date.now()
    ) {
      const { nextCredits } = remainingCredsPct(license);

      return (
        <>
          &nbsp;&nbsp;
          <span>{`${nextCredits ? ` +${nextCredits}` : "-"}`}</span>
        </>
      );
    }
  }

  return <Chip label={expireTxt} variant="outlined" />;
}
